import React, { useState, useEffect } from "react";
import { 
  Card, CardContent, Button, TextField, Select, MenuItem, 
  CircularProgress, Alert, Snackbar, Typography, Box, 
  IconButton, Tooltip, Paper, Divider 
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import config from "../../../config";

function ConfigureGa4() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingGa4, setSavingGa4] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [ga4PropertyId, setGa4PropertyId] = useState("");
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const clientId = config.clientId || "0486bc6d-6d15-4ea3-8504-adefe1512324";
  const SERVICE_ACCOUNT_EMAIL = "test-ga4@gen-lang-client-0541391584.iam.gserviceaccount.com";

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/client/store?client_id=${clientId}`, {
          headers: config.getHeaders()
        });
        const result = await response.json();
        
        if (result.success && result.data) {
          setStores(result.data);
          if (result.data.length > 0) {
            setSelectedStoreId(result.data[0].client_store_id);
            if (result.data[0].ga4_property_id) {
              setGa4PropertyId(result.data[0].ga4_property_id);
            }
          }
        } else {
          setError(result.message || "Failed to load stores");
        }
      } catch (err) {
        setError("Error connecting to server to load stores.");
      } finally {
        setLoading(false);
      }
    };
    fetchStores();
  }, [clientId]);

  const handleStoreChange = (e) => {
    const storeId = e.target.value;
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.client_store_id === storeId);
    if (store && store.ga4_property_id) {
      setGa4PropertyId(store.ga4_property_id);
    } else {
      setGa4PropertyId("");
    }
  };

  const handleSaveGa4 = async () => {
    if (!selectedStoreId || !ga4PropertyId) {
       setSnackbar({ open: true, message: "Please select a store and enter a Property ID.", severity: "warning" });
       return;
    }

    setSavingGa4(true);
    setError(null);
    try {
      const response = await fetch(`${config.API_BASE_URL}/client/store/ga4`, {
        method: "POST",
        headers: { ...config.getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ 
          client_id: clientId, 
          client_store_id: selectedStoreId,
          ga4_property_id: ga4PropertyId 
        })
      });
      const result = await response.json();
      
      if (result.success) {
         setSnackbar({ open: true, message: "GA4 Property Verified and Saved!", severity: "success" });
         setTimeout(() => navigate("/orders"), 1500);
      } else {
         setError(result.message || "Failed to connect GA4. Did you add the service account?");
      }
    } catch (err) {
       setError("Failed to communicate with the server.");
    } finally {
       setSavingGa4(false);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
    setSnackbar({ open: true, message: "Email copied to clipboard!", severity: "info" });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3} maxWidth="750px" margin="0 auto">
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/orders")}
        sx={{ mb: 3, color: "text.secondary", textTransform: "none", fontWeight: 600, fontSize: "14px", "&:hover": { color: "primary.main" } }}
      >
        Back to Orders
      </Button>

      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        
        {/* Header Section */}
        <Box sx={{ bgcolor: "#f8f9fa", p: 4, borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ bgcolor: "primary.main", color: "#fff", p: 1.5, borderRadius: 2, display: "flex" }}>
            <AnalyticsIcon fontSize="large" />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700" color="#1a1a1a">
              Connect Google Analytics 4
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Enable automated revenue tracking by linking your GA4 property to your Ask Bud-i store.
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>
          )}

          {/* Instructions Box */}
          <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, mb: 4, overflow: "hidden" }}>
            <Box sx={{ bgcolor: "#f4f6f8", px: 3, py: 1.5, borderBottom: "1px solid #e0e0e0" }}>
              <Typography variant="subtitle2" fontWeight="700" color="#454f5b">
                SETUP INSTRUCTIONS
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
                <Box sx={{ bgcolor: "#e3f2fd", color: "#1976d2", width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: 14, flexShrink: 0 }}>
                  1
                </Box>
                <Typography variant="body1" color="#333" sx={{ mt: 0.5 }}>
                  In your GA4 Dashboard, navigate to <strong>Admin &gt; Property Access Management</strong>.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ bgcolor: "#e3f2fd", color: "#1976d2", width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: 14, flexShrink: 0 }}>
                  2
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body1" color="#333" sx={{ mt: 0.5, mb: 1.5 }}>
                    Click the blue "+" button, select "Add users", and add this exact service account email as a <strong>Viewer</strong>:
                  </Typography>
                  <Box sx={{ 
                    display: "flex", alignItems: "center", justifyContent: "space-between", 
                    bgcolor: "#f8f9fa", border: "1px dashed #c4cdd5", borderRadius: 2, p: 1.5 
                  }}>
                    <Typography variant="body2" fontFamily="monospace" color="#d32f2f" fontWeight="600" sx={{ wordBreak: "break-all" }}>
                      {SERVICE_ACCOUNT_EMAIL}
                    </Typography>
                    <Tooltip title="Copy Email">
                      <IconButton onClick={handleCopyEmail} size="small" sx={{ color: "primary.main" }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>

          <Divider sx={{ my: 4 }} />

          {/* Form Fields */}
          <Typography variant="h6" fontWeight="600" mb={3} color="#1a1a1a">
            Configuration Details
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography variant="body2" fontWeight="600" color="#454f5b" mb={1}>
                Target Store
              </Typography>
              <Select
                fullWidth
                value={selectedStoreId}
                onChange={handleStoreChange}
                displayEmpty
                sx={{ borderRadius: 2, bgcolor: "#fff", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4cdd5" } }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.client_store_id} value={store.client_store_id}>
                    {store.store_name} {store.ga4_property_id ? " (Currently Configured)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight="600" color="#454f5b" mb={1}>
                GA4 Property ID
              </Typography>
              <TextField
                fullWidth
                placeholder="e.g. 123456789"
                variant="outlined"
                value={ga4PropertyId}
                onChange={(e) => setGa4PropertyId(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                Found in GA4 Admin &gt; Property Settings &gt; Property ID. Do not include 'G-' measurement IDs.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 5 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={handleSaveGa4}
              disabled={savingGa4 || !ga4PropertyId || !selectedStoreId}
              sx={{ 
                py: 1.8, 
                fontWeight: "700", 
                fontSize: "15px",
                borderRadius: 2,
                boxShadow: "0 4px 14px 0 rgba(25, 118, 210, 0.39)",
                textTransform: "none",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(25, 118, 210, 0.23)"
                }
              }}
            >
              {savingGa4 ? <CircularProgress size={24} color="inherit" /> : "Verify & Connect Integration"}
            </Button>
          </Box>

        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ConfigureGa4;
